"use client";
import { useEffect, useState, useRef } from "react";
import { getWithTokenAPI, deleteAPI, postWithTokenAPI } from "../../utils/Api";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  CircularProgress,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TextField,
  Pagination,
  InputAdornment,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";
import FlightIcon from "@mui/icons-material/Flight";
import WorkIcon from "@mui/icons-material/Work"; // For service related icon
import SubjectIcon from "@mui/icons-material/Subject";
import InfoIcon from "@mui/icons-material/Info";

const DroneService = () => {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [openViewDialog, setOpenViewDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
   const permissions = JSON.stringify(localStorage.getItem("permissions"))
 

  // Debounce timeout ref
  const debounceTimeoutRef = useRef(null);

  // Form states
  const [formData, setFormData] = useState({
    service_type: "",
    rate_per_acre: "",
    notes: "",
    vat: "",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [servicesPerPage] = useState(5);

  const primaryGreen = "#DC3545";
  const lightGreen = "#ffe5e5";
  const darkGreen = "#b02a37";

  const fetchServices = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await getWithTokenAPI(token, "/drone-service/find-all");
      if (res.statusCode === 200) {
        setServices(res.data.services || []);
      } else {
        toast.error(res.message || "Failed to load drone services.");
        setServices([]);
      }
    } catch (error) {
      console.error("Fetch services error:", error);
      toast.error("Failed to load services due to a network error.");
      setServices([]);
    } finally {
      setLoading(false);
    }
  };

  // Handle search with API call
  const handleSearch = async (searchValue) => {
    const trimmedValue = searchValue.trim();
    if (!trimmedValue) {
      clearSearch();
      return;
    }

    const token = localStorage.getItem("token");
    setLoading(true);
    try {
      const res = await getWithTokenAPI(
        token,
        `/drone-service/search?service_type=${encodeURIComponent(trimmedValue)}`
      );

      if (res.statusCode === 200) {
        setSearchResults(res.data.services || []);
        setIsSearching(true);
        setCurrentPage(1); // Reset to first page for new search results
      } else {
        toast.error(res.message || "No matching services found");
        setSearchResults([]);
        setIsSearching(true);
      }
    } catch (error) {
      console.error("Search error:", error);
      toast.error("Failed to search services");
      setSearchResults([]);
      setIsSearching(false);
    } finally {
      setLoading(false);
    }
  };

  // Clear search
  const clearSearch = () => {
    setSearchTerm("");
    setIsSearching(false);
    setSearchResults([]);
    setCurrentPage(1);
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    fetchServices();
  };

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (!value.trim()) {
      clearSearch();
      return;
    }
    debounceTimeoutRef.current = setTimeout(() => {
      handleSearch(value);
    }, 500);
  };

  const handleSearchSubmit = () => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    if (searchTerm.trim()) {
      handleSearch(searchTerm);
    } else {
      clearSearch();
    }
  };
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  const createService = async () => {
    const token = localStorage.getItem("token");
    setLoading(true);

    const rate = parseFloat(formData.rate_per_acre) || 0;
    const vatAmount = rate * 0.16;

    try {
      const res = await postWithTokenAPI(token, "/drone-service/create", {
        service_type: formData.service_type,
        rate_per_acre: rate,
        notes: formData.notes,
        vat: vatAmount,
      });

      if (res.statusCode === 200) {
        toast.success("Service created successfully!");
        setOpenCreateDialog(false);
        setFormData({
          service_type: "",
          rate_per_acre: "",
          notes: "",
          vat: "",
        });

        await fetchServices();
        if (isSearching && searchTerm.trim()) {
          await handleSearch(searchTerm);
        }
      } else {
        toast.error(res.message || "Failed to create service.");
      }
    } catch (error) {
      console.error("Create service error:", error);
      toast.error("Failed to create service due to a network error.");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    if (name === "rate_per_acre") {
      const rate = parseFloat(value) || 0;
      const vatAmount = rate * 0.16;
      setFormData((prev) => ({
        ...prev,
        vat: vatAmount.toFixed(2),
      }));
    }
  };

  const deleteService = async () => {
    if (!selectedService) return;
    const token = localStorage.getItem("token");
    setLoading(true);

    try {
      const res = await deleteAPI(token, "/drone-service/delete", {
        _id: selectedService._id,
      });

      if (res.statusCode === 200) {
        toast.success("Service marked as deleted successfully!");
        setOpenDeleteDialog(false);

        await fetchServices();
        if (isSearching && searchTerm.trim()) {
          await handleSearch(searchTerm);
        }
      } else if (res.statusCode === 406) {
        toast.warning(
          "This service was already marked as deleted. Refreshing list..."
        );
        await fetchServices();
        if (isSearching && searchTerm.trim()) {
          await handleSearch(searchTerm);
        }
      } else {
        toast.error(res.message || "Failed to delete service.");
      }
    } catch (error) {
      console.error("Delete service error:", error);
      toast.error("Failed to delete service due to a network error.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      service_type: "",
      rate_per_acre: "",
      notes: "",
      vat: "",
    });
    setOpenCreateDialog(true);
  };

  const currentServices = isSearching ? searchResults : services;
  const indexOfLastService = currentPage * servicesPerPage;
  const indexOfFirstService = indexOfLastService - servicesPerPage;
  const paginatedServices = currentServices.slice(
    indexOfFirstService,
    indexOfLastService
  );
  const totalPages = Math.ceil(currentServices.length / servicesPerPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  useEffect(() => {
    fetchServices();
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ p: 3, width: "100%" }}>
      <ToastContainer position="top-center" autoClose={1500} />

      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          mb: 3,
          p: 2,
          backgroundColor: lightGreen,
          borderRadius: 2,
          borderLeft: `5px solid ${primaryGreen}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <FlightIcon sx={{ color: primaryGreen, fontSize: 40, mr: 2 }} />
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ color: primaryGreen, fontWeight: "bold" }}
            >
              Drone Services
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary" }}>
              Manage Drone Services
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Search and Add Service Section */}
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search by service type..."
          value={searchTerm}
          onChange={handleSearchInputChange}
          onKeyPress={handleKeyPress}
          InputProps={{
            sx: { height: 40 },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: primaryGreen, cursor: "pointer" }}
                  onClick={handleSearchSubmit}
                />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={clearSearch} size="small">
                  <ClearIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        {
          permissions.includes("create_dronemanagement") && (
            <Box sx={{ ml: "auto" }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenCreateDialog}
            sx={{
              bgcolor: primaryGreen,
              "&:hover": { bgcolor: darkGreen },
            }}
          >
            Add
          </Button>
        </Box>
          )}
        
      </Box>

      {/* Main Content Area */}
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 200,
          }}
        >
          <CircularProgress sx={{ color: primaryGreen }} />
          <Typography sx={{ ml: 2, color: primaryGreen }}>
            {isSearching
              ? "Searching drone services..."
              : "Loading drone services..."}
          </Typography>
        </Box>
      ) : paginatedServices.length > 0 ? (
        <>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 3,
              borderRadius: 2,
              border: `1px solid ${lightGreen}`,
            }}
          >
            <Table sx={{ minWidth: 650 }} aria-label="drone services table">
              <TableHead>
                <TableRow sx={{ backgroundColor: lightGreen }}>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                      color: "text.secondary",
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                      color: "text.secondary",
                    }}
                  >
                    Service Type
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                      color: "text.secondary",
                    }}
                  >
                    Rate Per Acre(Ksh)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                      color: "text.secondary",
                    }}
                  >
                    Notes
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                      color: "text.secondary",
                    }}
                  >
                    VAT(Ksh)
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: "bold",
                      textAlign: "left",
                      color: "text.secondary",
                    }}
                  >
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedServices.map((service, index) => (
                  <TableRow key={service._id} hover>
                    <TableCell sx={{ textAlign: "left" }}>
                      {index + 1 + (currentPage - 1) * servicesPerPage}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left",     fontWeight: "bold",
            color: primaryGreen,
            textDecoration: 'none',
            cursor: 'pointer', }}      onClick={() => {
                          setSelectedService(service);
                          setOpenViewDialog(true);
                        }}>
                      {service.service_type}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {service.rate_per_acre.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {service.notes}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left" }}>
                      {service.vat.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ textAlign: "left", display: "flex" }}>
                      <IconButton
                        onClick={() => {
                          setSelectedService(service);
                          setOpenViewDialog(true);
                        }}
                        sx={{ color: primaryGreen }}
                      >
                        <VisibilityIcon />
                      </IconButton>
                      {
          permissions.includes("delete_dronemanagement") && (
 
                      <IconButton
                        onClick={() => {
                          setSelectedService(service);
                          setOpenDeleteDialog(true);
                        }}
                        sx={{ color: primaryGreen }}
                      >
                        <DeleteIcon />
                      </IconButton>
          )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: "flex", justifyContent: "end", mt: 3, pb: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="green"
              sx={{
                "& .MuiPaginationItem-root": { color: primaryGreen },
                "& .MuiPaginationItem-root.Mui-selected": {
                  backgroundColor: primaryGreen,
                  color: "white",
                },
              }}
            />
          </Box>
        </>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            p: 4,
            textAlign: "center",
          }}
        >
          <Box sx={{ color: "text.disabled" }}>
            <FlightIcon sx={{ fontSize: 60, mb: 2 }} />
            <Typography variant="h6" sx={{ mb: 1 }}>
              {isSearching
                ? `No services found for "${searchTerm}"`
                : "No drone services available"}
            </Typography>
            {isSearching && (
              <Button
                variant="outlined"
                onClick={clearSearch}
                sx={{
                  mt: 2,
                  color: primaryGreen,
                  borderColor: primaryGreen,
                  "&:hover": { borderColor: darkGreen },
                }}
              >
                Show All Services
              </Button>
            )}
          </Box>
        </Box>
      )}

      {/* View Service Dialog */}
      <Dialog
        open={openViewDialog}
        onClose={() => setOpenViewDialog(false)}
        maxWidth="md"
        fullWidth
      >
        {selectedService && (
          <>
            <DialogTitle
              sx={{
                backgroundColor: primaryGreen,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                {/* Changed icon for Service Details */}
                <WorkIcon sx={{ mr: 1 }} />
                Service Details
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setOpenViewDialog(false)}
              >
                <ClearIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${lightGreen}`,
                      borderRadius: 2,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: darkGreen,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {/* Changed icon for Service Information */}
                      <InfoIcon sx={{ mr: 1 }} />
                      Service Information
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                      {/* Aligned using flexbox and fixed width for the label part */}
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          mb: 1,
                        }}
                      >
                        <span style={{ minWidth: "120px" }}>Service Type:</span>{" "}
                        {/* Fixed width for alignment */}
                        <span style={{ fontWeight: "bold" }}>
                          {selectedService.service_type}
                        </span>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          mb: 1,
                        }}
                      >
                        <span style={{ minWidth: "120px" }}>
                          Rate Per Acre(Ksh):
                        </span>{" "}
                        {/* Fixed width for alignment */}
                        <span style={{ fontWeight: "bold" }}>
                          {selectedService.rate_per_acre.toFixed(2)}
                        </span>
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          display: "flex",
                          mb: 1,
                        }}
                      >
                        <span style={{ minWidth: "120px" }}>VAT(Ksh) (16%):</span>{" "}
                        {/* Fixed width for alignment */}
                        <span style={{ fontWeight: "bold" }}>
                          {selectedService.vat.toFixed(2)}
                        </span>
                      </Typography>
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      border: `1px solid ${lightGreen}`,
                      borderRadius: 2,
                      height: "100%",
                    }}
                  >
                    <Typography
                      variant="h6"
                      sx={{
                        mb: 2,
                        color: darkGreen,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {/* Changed icon for Additional Details */}
                      <SubjectIcon sx={{ mr: 1 }} />
                      Additional Details
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Notes:</strong> {selectedService.notes}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setOpenViewDialog(false)}
                variant="outlined"
                sx={{ color: darkGreen, borderColor: darkGreen }}
              >
                Close
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Service Dialog */}

      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            backgroundColor: primaryGreen,
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AddIcon sx={{ mr: 1 }} />
            Create New Service
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={() => setOpenCreateDialog(false)}
          >
            <ClearIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 3 }}>
          {/* First Row - 3 Fields in Flex */}
          <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
            {/* Service Type */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Service Type"
                name="service_type"
                value={formData.service_type}
                onChange={handleInputChange}
                required
              />
            </Box>

            {/* Rate Per Acre */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="Rate Per Acre (Ksh)"
                name="rate_per_acre"
                type="number"
                value={formData.rate_per_acre}
                onChange={handleInputChange}
                required
                inputProps={{ min: 0, step: "0.01" }}
              />
            </Box>

            {/* VAT */}
            <Box sx={{ flex: 1 }}>
              <TextField
                fullWidth
                label="VAT (16%) (Ksh)"
                name="vat"
                type="number"
                value={formData.vat}
                onChange={handleInputChange}
                required
                disabled
                InputProps={{ readOnly: true }}
              />
            </Box>
          </Box>

          {/* Second Row - Notes (Full Width) */}
          <Box sx={{ width: "100%" }}>
            <TextField
              fullWidth
              label="Notes"
              name="notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={handleInputChange}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setOpenCreateDialog(false)}
            variant="outlined"
            sx={{ color: darkGreen, borderColor: darkGreen }}
          >
            Cancel
          </Button>
          <Button
            onClick={createService}
            variant="contained"
            sx={{ bgcolor: primaryGreen, "&:hover": { bgcolor: darkGreen } }}
            disabled={
              loading || !formData.service_type || !formData.rate_per_acre
            }
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              "Create Service"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        maxWidth="xs"
      >
        {selectedService && (
          <>
            <DialogTitle
              sx={{
                backgroundColor: primaryGreen,
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <DeleteIcon sx={{ mr: 1 }} />
                Confirm Delete
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={() => setOpenDeleteDialog(false)}
              >
                <ClearIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ p: 3 }}>
              <Typography>
                Are you sure you want to delete the service type "
                <strong style={{ color: darkGreen }}>
                  {selectedService.service_type}
                </strong>
                "? This action cannot be undone.
              </Typography>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button
                onClick={() => setOpenDeleteDialog(false)}
                variant="outlined"
                sx={{ color: darkGreen, borderColor: darkGreen }}
              >
                Cancel
              </Button>
              <Button
                onClick={deleteService}
                variant="contained"
                color="error"
                sx={{ bgcolor: primaryGreen }}
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Delete"
                )}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default DroneService;